// geminiProcessor.ts - Clean Gemini AI integration

import { CheckResult } from './checkDefinitions';
import { renderPrompt } from './promptDefinitions';
import { arrayBufferToBase64 } from './utils';
import { backendGenerate } from './backendAI';

// All Gemini calls are proxied through the platform backend (a4idemo.pkfaudit.ai),
// which holds the API key. Nothing here reads VITE_GEMINI_API_KEY anymore.

// Cache for PDF base64 data to avoid repeated conversions
const pdfCache = new Map<string, string>();

// arrayBufferToBase64 is now imported from utils.ts

/**
 * Get PDF as Base64 string with caching
 */
async function getPdfAsBase64(pdfFile: File): Promise<string> {
  const cacheKey = `${pdfFile.name}_${pdfFile.size}`;
  
  if (pdfCache.has(cacheKey)) {
    console.log(`Using cached PDF data for ${pdfFile.name}`);
    return pdfCache.get(cacheKey)!;
  }
  
  const pdfArrayBuffer = await pdfFile.arrayBuffer();
  const pdfBase64 = arrayBufferToBase64(pdfArrayBuffer);
  
  pdfCache.set(cacheKey, pdfBase64);
  console.log(`Cached PDF data for ${pdfFile.name}`);
  
  return pdfBase64;
}

/**
 * Call Gemini API with retry logic
 */
function normalizeModelId(input?: string): string {
  const model = (input || '').trim().toLowerCase();
  const mappings: Record<string, string> = {
    'gemini-1.5-flash-exp': 'gemini-2.5-flash',
    'gemini-2.0-flash-exp': 'gemini-2.5-flash',
    'gemini-1.5-flash-001': 'gemini-2.5-flash',
    'gemini-1.5-flash': 'gemini-2.5-flash',
    'gemini-2.5-flash': 'gemini-2.5-flash',
    'gemini-2.0-flash': 'gemini-2.5-flash',
    'gemini-2.5-flash-lite': 'gemini-2.5-flash-lite',
    'gemini-2.5-flash': 'gemini-2.5-flash',
    'gemini-2.5-pro-preview': 'gemini-2.5-pro',
    'gemini-2.5-pro-preview-05-20': 'gemini-2.5-pro',
  };
  if (mappings[model]) return mappings[model];
  if (model.startsWith('gemini-') && !model.includes('exp')) {
    return model;
  }
  if (model.endsWith('-exp')) return model.replace(/-exp$/, '');
  return 'gemini-2.5-flash';
}

async function callGeminiAPI(
  prompt: string,
  pdfBase64: string,
  maxRetries: number = 3,
  model: string = 'gemini-2.5-flash',
  temperature: number = 0.1,
  abortSignal?: AbortSignal
): Promise<any> {
  // Native PDF analysis is proxied through the backend (key stays server-side).
  const normalizedModel = normalizeModelId(model);

  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);

      const combinedSignal = abortSignal ?
        AbortSignal.any([controller.signal, abortSignal]) :
        controller.signal;

      const data = await backendGenerate({
        prompt,
        pdfBase64,
        model: normalizedModel,
        temperature,
        maxOutputTokens: 32768,
        signal: combinedSignal,
      });

      clearTimeout(timeoutId);

      if (!data.candidates?.[0]?.content?.parts?.[0]?.text) {
        throw new Error("Unexpected response format from Gemini API");
      }
      return data;
    } catch (error) {
      retryCount++;

      if (error instanceof Error && error.name === 'AbortError') {
        console.warn(`Request aborted for model ${normalizedModel} (attempt ${retryCount}/${maxRetries})`);
        if (retryCount >= maxRetries) {
          throw new Error(`Request timeout after ${maxRetries} attempts. The model may be overloaded or the request is too complex.`);
        }
      } else {
        console.warn(`Retry ${retryCount}/${maxRetries} due to: ${error}`);
        if (retryCount >= maxRetries) {
          throw error;
        }
      }

      const isRateLimited = error instanceof Error && error.message.includes('429');
      const delay = isRateLimited
        ? 8000 * Math.pow(2, retryCount)
        : error instanceof Error && error.name === 'AbortError'
          ? 2000 * Math.pow(2, retryCount)
          : 1000 * Math.pow(2, retryCount);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw new Error(`Failed to get response from Gemini API after ${maxRetries} attempts`);
}

/**
 * Parse Gemini API response into CheckResult
 */
function parseGeminiResponse(responseText: string, checkType: string): CheckResult {
  const lines = responseText.split('\n');
  const firstLine = lines[0].trim().toLowerCase();
  let explanation = lines.slice(1).join('\n').trim();

  // Determine compliance - handle multiple response formats
  let isCompliant = false;
  let isWarning = false;
  
  // Check for warning response format (including "Check" for cross_references_correctness)
  if (firstLine === 'warning' || 
      firstLine.includes('warning') ||
      firstLine === '⚠️ warning' ||
      firstLine === 'warning —' ||
      firstLine === 'warning -' ||
      firstLine === 'check') {
    isWarning = true;
  }
  
  // Check for various compliant response formats (but not if it's a warning/check)
  else if (firstLine === 'yes' || 
      firstLine === 'compliant' || 
      firstLine.includes('compliant') ||
      firstLine === '✅ compliant' ||
      firstLine === 'compliant —' ||
      firstLine === 'compliant -') {
    isCompliant = true;
  }
  
  // Check for non-compliant response formats
  else if (firstLine === 'no' || 
      firstLine === 'non-compliant' || 
      firstLine === 'non compliant' ||
      firstLine.includes('non-compliant') ||
      firstLine.includes('non compliant') ||
      firstLine === '❌ non-compliant' ||
      firstLine === '❌ non compliant' ||
      firstLine === 'non-compliant —' ||
      firstLine === 'non compliant —') {
    isCompliant = false;
  }
  
  // Check for cases where AI cannot perform analysis due to missing data
  if (responseText.toLowerCase().includes('cannot calculate') || 
      responseText.toLowerCase().includes('cannot perform') ||
      responseText.toLowerCase().includes('not included in the document') ||
      responseText.toLowerCase().includes('data not available') ||
      responseText.toLowerCase().includes('insufficient data')) {
    isCompliant = false;
    explanation = responseText.trim(); // Use the full AI response as explanation
  }

  // Only enhance if the response is truly empty
  if (!explanation || explanation.trim().length < 10) {
    explanation = 'Analysis completed based on document review.';
  }

  // Extract additional data for specific checks
  let extractedData = null;
  
  switch (checkType) {
    case 'signature_date':
      if (lines.length > 1) {
        extractedData = { signatureBlock: lines.slice(1).join('\n') };
      }
      break;
      
    case 'company_name_consistency':
      if (lines.length >= 3) {
        extractedData = {
          totalMentions: lines[1].trim(),
          variations: lines[2].trim(),
          details: lines.slice(3).join('\n').trim()
        };
      }
      break;
      
    case 'caro_interlink':
    case 'ifc_interlink':
      if (lines.length >= 2) {
        extractedData = { paragraphComparison: lines[1].trim() };
      }
      break;
  }

  // Post-process for specific checks
  let finalIsCompliant = isCompliant;
  let finalIsWarning = isWarning;
  let finalExplanation = explanation || `${checkType} check completed`;

  if (checkType === 'rs_in_units') {
    // If the model did not set the first line clearly, infer from explanation
    const t = finalExplanation.toLowerCase();
    const hasCurrency = /(\brs\.?\b|₹|\binr\b)/.test(t);
    const hasMeasurement = /(lakh|lakhs|crore|crores|thousand|thousands)/.test(t);
    // Only currency consistency matters; measurement may vary by table
    const mentionsInconsistent = /(inconsistent\s+currency|mixed\s+currency|different\s+currency\s+labels|not\s+consistent\s+currency)/.test(t);
    const mentionsMissing = /(missing\s+label|label\s+missing|no\s+label|missing\s+currency|missing\s+unit)/.test(t);

    if (mentionsInconsistent || mentionsMissing || !hasCurrency || !hasMeasurement) {
      finalIsCompliant = false;
    } else if (hasCurrency && hasMeasurement && !mentionsInconsistent) {
      finalIsCompliant = true;
    }
  }

  if (checkType === 'major_events_notes') {
    const t = finalExplanation.toLowerCase();
    const mentionsAdequateDisclosure = /(explained in notes|adequate disclosure|clearly explains|note\s+\d+|explained|disclosed)/.test(t);
    const indicatesNoEventsOrNoWarning = /(no major events|no significant events|no warning is necessary|no compliance warning|no such events)/.test(t);
    const indicatesMissing = /(no note|no explanation|not disclosed|missing\s+note|missing\s+disclosure|absent\s+note|lack\s+of\s+disclosure)/.test(t);
    const isGeneric = /check completed|analysis complete|no additional details/i.test(finalExplanation) || finalExplanation.length < 40;

    if (indicatesMissing) {
      finalIsCompliant = false;
    } else if (indicatesNoEventsOrNoWarning || mentionsAdequateDisclosure) {
      finalIsCompliant = true;
    } else if (isGeneric) {
      // If the model provided no details and did not flag missing disclosure, default to compliant
      finalIsCompliant = true;
      finalExplanation = 'No missing major events indicators detected; disclosure appears adequate.';
    }
  }

  if (checkType === 'quarterly_ytd_figures_notes') {
    const t = finalExplanation.toLowerCase();
    // Check for contradictory analysis that indicates non-compliance
    const indicatesNonCompliance = /(does not|doesn't|lack|missing|omission|gap|significant compliance issue|critical gap|not explicitly|fails to|incomplete|insufficient)/.test(t);
    const indicatesCompliance = /(present|included|contains|explicitly states|clearly shows|adequate|sufficient)/.test(t);
    
    // If the analysis contains strong indicators of non-compliance, override the first line
    if (indicatesNonCompliance && !indicatesCompliance) {
      finalIsCompliant = false;
    }
  }

  return {
    isCompliant: finalIsCompliant,
    isWarning: finalIsWarning,
    explanation: finalExplanation,
    confidence: 0.9,
    extractedData,
    detail: lines.length > 2 ? lines.slice(2).join('\n').trim() : ""
  };
}

/**
 * Process a single document check using Gemini AI
 */
export async function processSingleDocumentCheck(
  pdfFile: File,
  checkType: string,
  promptKey: string,
  parameters: Record<string, any>,
  model?: string,
  temperature?: number,
  abortSignal?: AbortSignal
): Promise<CheckResult> {
  try {
    console.log(`Processing ${checkType} check with Gemini AI`);

    // Get PDF as Base64
    const pdfBase64 = await getPdfAsBase64(pdfFile);

    // Render prompt with parameters
    const prompt = renderPrompt(promptKey, parameters);

    // Call Gemini API with custom model and temperature if provided
    const data = await callGeminiAPI(prompt, pdfBase64, 3, model, temperature, abortSignal);

    // Parse response
    const responseText = data.candidates[0].content.parts[0].text;
    const result = parseGeminiResponse(responseText, checkType);

    console.log(`Successfully processed ${checkType} check:`, result);
    return result;

  } catch (error) {
    console.error(`Error processing ${checkType} check:`, error);

    return {
      isCompliant: false,
      explanation: `Error processing check: ${error instanceof Error ? error.message : String(error)}`,
      confidence: 0.5,
      extractedData: null,
      detail: ""
    };
  }
}

/**
 * Clear PDF cache for memory management
 */
export function clearPdfCache(): void {
  pdfCache.clear();
  console.log('PDF cache cleared');
}

/**
 * Get cache statistics
 */
export function getCacheStats(): { size: number; keys: string[] } {
  return {
    size: pdfCache.size,
    keys: Array.from(pdfCache.keys())
  };
}

/**
 * Call Gemini API specifically for auditor resignation checks using different model
 */
async function callAuditorResignationGeminiAPI(prompt: string, pdfBase64: string, maxRetries: number = 3): Promise<any> {
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 45000); // Longer timeout for pro model

      const data = await backendGenerate({
        prompt,
        pdfBase64,
        model: 'gemini-2.5-pro',
        temperature: 0.05,
        maxOutputTokens: 4096,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Validate response structure
      if (!data.candidates?.[0]?.content?.parts?.[0]?.text) {
        throw new Error("Unexpected response format from Auditor Resignation Gemini API");
      }
      return data;
    } catch (error) {
      retryCount++;
      console.warn(`Auditor Resignation Retry ${retryCount}/${maxRetries} due to: ${error}`);

      if (retryCount >= maxRetries) {
        throw error;
      }

      // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, 1500 * Math.pow(2, retryCount)));
    }
  }
  
  throw new Error(`Failed to get response from Auditor Resignation Gemini API after ${maxRetries} attempts`);
}

/**
 * Process auditor resignation checks using separate Gemini model
 */
export async function processAuditorResignationCheck(
  pdfFile: File,
  checkType: string,
  promptKey: string,
  parameters: Record<string, any>
): Promise<CheckResult> {
  try {
    console.log(`🔍 Processing ${checkType} check with SEPARATE Gemini model for auditor resignation`);

    // Get PDF as Base64
    const pdfBase64 = await getPdfAsBase64(pdfFile);

    // Render prompt with parameters
    const prompt = renderPrompt(promptKey, parameters);

    // Call separate Gemini API for auditor resignation
    const data = await callAuditorResignationGeminiAPI(prompt, pdfBase64);

    // Parse response
    const responseText = data.candidates[0].content.parts[0].text;
    const result = parseAuditorResignationResponse(responseText, checkType);

    console.log(`✅ Successfully processed ${checkType} check with separate model:`, result);
    return result;

  } catch (error) {
    console.error(`❌ Error processing ${checkType} check with separate model:`, error);

    return {
      isCompliant: false,
      explanation: `Error processing auditor resignation check: ${error instanceof Error ? error.message : String(error)}`,
      confidence: 0.5,
      extractedData: null,
      detail: ""
    };
  }
}

/**
 * Parse auditor resignation response into CheckResult
 */
function parseAuditorResignationResponse(responseText: string, checkType: string): CheckResult {
  const lines = responseText.split('\n').filter(line => line.trim());
  
  console.log(`📄 Parsing auditor resignation response (${lines.length} lines):`, lines);

  // Default structure
  const result: any = {
    isCompliant: false,
    explanation: responseText,
    confidence: 0.9,
    extractedData: {},
    detail: ""
  };

  // Parse based on check type
  if (checkType.includes('annual_report_auditor_resignation')) {
    // Parse Annual Report response
    const auditorResignationData: any = {};
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      
      if (trimmedLine.startsWith('Has_Auditor_Resignation:')) {
        const value = trimmedLine.split(':')[1]?.trim().toLowerCase();
        auditorResignationData.hasAuditorResignation = value === 'yes';
      } else if (trimmedLine.startsWith('Resignation_Details:')) {
        auditorResignationData.resignationDetails = trimmedLine.split(':')[1]?.trim();
      } else if (trimmedLine.startsWith('Filing_References:')) {
        auditorResignationData.filingReferences = trimmedLine.split(':')[1]?.trim();
      } else if (trimmedLine.startsWith('Location_In_Report:')) {
        auditorResignationData.locationInReport = trimmedLine.split(':')[1]?.trim();
      } else if (trimmedLine.startsWith('Resignation_Reason:')) {
        auditorResignationData.resignationReason = trimmedLine.split(':')[1]?.trim();
      }
    });
    
    result.extractedData = { auditorResignationData };
    result.isCompliant = true; // This step always succeeds if parsing works
    
  } else if (checkType.includes('caro_clause_xviii')) {
    // Parse CARO response
    const caroClauseXVIIIData: any = {};
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      
      if (trimmedLine.startsWith('Has_Clause_XVIII:')) {
        const value = trimmedLine.split(':')[1]?.trim().toLowerCase();
        caroClauseXVIIIData.hasClauseXVIII = value === 'yes';
      } else if (trimmedLine.startsWith('Clause_XVIII_Status:')) {
        caroClauseXVIIIData.clauseXVIIIStatus = trimmedLine.split(':')[1]?.trim();
      } else if (trimmedLine.startsWith('Auditor_Resignation_Mentioned:')) {
        const value = trimmedLine.split(':')[1]?.trim().toLowerCase();
        caroClauseXVIIIData.auditorResignationMentioned = value === 'yes';
      } else if (trimmedLine.startsWith('Clause_Content_Summary:')) {
        caroClauseXVIIIData.clauseContentSummary = trimmedLine.split(':')[1]?.trim();
      } else if (trimmedLine.startsWith('Compliance_Indication:')) {
        caroClauseXVIIIData.complianceIndication = trimmedLine.split(':')[1]?.trim();
      }
    });
    
    result.extractedData = { caroClauseXVIIIData };
    result.isCompliant = true; // This step always succeeds if parsing works
  }

  console.log(`📊 Parsed auditor resignation data:`, result.extractedData);
  return result;
}

/**
 * Process multiple documents check with custom prompt (for Results Analyzer)
 */
export async function processMultiDocumentCheck(
  primaryFile: File,
  secondaryFile: File,
  checkType: string,
  customPrompt: string,
  model?: string,
  temperature?: number,
  abortSignal?: AbortSignal
): Promise<CheckResult> {
  try {
    console.log(`Processing ${checkType} multi-document check with custom prompt using Gemini AI`);

    // Get both PDFs as Base64
    const primaryPdfBase64 = await getPdfAsBase64(primaryFile);
    // Warm the cache for the secondary doc (not currently sent to API)
    await getPdfAsBase64(secondaryFile);

    // Create a combined prompt that references both documents
    const multiDocumentPrompt = `${customPrompt}

DOCUMENT 1: ${primaryFile.name}
DOCUMENT 2: ${secondaryFile.name}

You are analyzing TWO SEPARATE DOCUMENTS. You must compare data between them:
- Document 1 contains current quarter data
- Document 2 contains previous year data
- Compare opening balance from Document 1 with closing balance from Document 2`;

    // Call Gemini API with the primary document and multi-document prompt
    let data: any;
    try {
      data = await callGeminiAPI(multiDocumentPrompt, primaryPdfBase64, 3, normalizeModelId(model), temperature, abortSignal);
    } catch (err) {
      const message = String(err);
      if (message.includes('404')) {
        console.warn(`Model ${model} not found (404). Falling back to default model.`);
        data = await callGeminiAPI(multiDocumentPrompt, primaryPdfBase64, 3, 'gemini-2.5-flash', temperature, abortSignal);
      } else {
        throw err;
      }
    }

    // Parse the response
    const response = data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response generated';

    // Parse status consistently (supports "Check" as warning)
    const parsed = parseGeminiResponse(response, checkType);

    // For Results Analyzer, preserve the full raw response for display/debug
    return {
      ...parsed,
      explanation: response,
      detail: response,
    };
  } catch (error) {
    console.error(`Error processing multi-document check ${checkType}:`, error);
    return {
      isCompliant: false,
      explanation: `Error processing multi-document analysis: ${error instanceof Error ? error.message : 'Unknown error'}`,
      detail: `Error processing multi-document analysis: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Process document check with custom prompt (for Results Analyzer)
 */
export async function processCustomDocumentCheck(
  pdfFile: File,
  checkType: string,
  customPrompt: string,
  model?: string,
  temperature?: number,
  abortSignal?: AbortSignal
): Promise<CheckResult> {
  try {
    console.log(`Processing ${checkType} check with custom prompt using Gemini AI`);
    console.log(`File: ${pdfFile.name}, Size: ${pdfFile.size} bytes`);
    console.log(`Model: ${model}, Temperature: ${temperature}`);
    console.log(`Custom Prompt:`, customPrompt);

    // Get PDF as Base64
    const pdfBase64 = await getPdfAsBase64(pdfFile);
    console.log(`PDF converted to Base64, length: ${pdfBase64.length}`);

    // Call Gemini API with custom prompt, model and temperature
    // Try with normalized model first
    let data: any;
    try {
      console.log(`Calling Gemini API with model: ${normalizeModelId(model)}`);
      data = await callGeminiAPI(customPrompt, pdfBase64, 3, normalizeModelId(model), temperature, abortSignal);
    } catch (err) {
      const message = String(err);
      // Fallback to default model if model not found (404)
      if (message.includes('404')) {
        console.warn(`Model ${model} not found (404). Falling back to default model.`);
        data = await callGeminiAPI(customPrompt, pdfBase64, 3, 'gemini-2.5-flash', temperature, abortSignal);
      } else {
        throw err;
      }
    }

    // Parse response
    const responseText = data.candidates[0].content.parts[0].text;
    console.log(`Raw Gemini Response for ${checkType}:`, responseText);
    

    if (checkType.includes('_ob') || checkType.includes('cb_py')) {
      const result = {
        isCompliant: true,
        explanation: responseText.trim(),
        detail: responseText.trim()
      };
      console.log(`Successfully processed ${checkType} extraction:`, result);
      return result;
    }
    
    // For other checks, use normal parsing
    const result = parseGeminiResponse(responseText, checkType);
    console.log(`Successfully processed ${checkType} check with custom prompt:`, result);
    return result;

  } catch (error) {
    console.error(`Error processing ${checkType} check with custom prompt:`, error);

    return {
      isCompliant: false,
      explanation: `Error processing check: ${error instanceof Error ? error.message : String(error)}`,
      confidence: 0.5,
      extractedData: null,
      detail: ""
    };
  }
}

/**
 * Call Gemini API with text-only (no PDF)
 */
async function callGeminiAPITextOnly(
  prompt: string, 
  maxRetries: number = 3,
  model: string = 'gemini-2.5-flash',
  temperature: number = 0.1,
  abortSignal?: AbortSignal
): Promise<any> {
  // Text-only calls are proxied through the backend (key stays server-side).
  const normalizedModel = normalizeModelId(model);

  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

      // Combine abort signals if provided
      const combinedSignal = abortSignal ?
        AbortSignal.any([controller.signal, abortSignal]) :
        controller.signal;

      const data = await backendGenerate({
        prompt,
        model: normalizedModel,
        temperature,
        maxOutputTokens: 8192,
        signal: combinedSignal,
      });

      clearTimeout(timeoutId);

      // Validate response structure
      if (!data.candidates?.[0]?.content?.parts?.[0]?.text) {
        throw new Error("Unexpected response format from Gemini API");
      }
      return data;
    } catch (error) {
      retryCount++;
      
      // Handle specific abort errors differently
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn(`Request aborted for model ${normalizedModel} (attempt ${retryCount}/${maxRetries})`);
        if (retryCount >= maxRetries) {
          throw new Error(`Request timeout after ${maxRetries} attempts. The model may be overloaded or the request is too complex.`);
        }
      } else {
        console.warn(`Retry ${retryCount}/${maxRetries} due to: ${error}`);
        if (retryCount >= maxRetries) {
          throw error;
        }
      }

      // Exponential backoff; give rate-limit (429) errors a much longer cool-down
      const isRateLimited = error instanceof Error && error.message.includes('429');
      const delay = isRateLimited
        ? 8000 * Math.pow(2, retryCount)
        : error instanceof Error && error.name === 'AbortError'
          ? 2000 * Math.pow(2, retryCount)
          : 1000 * Math.pow(2, retryCount);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw new Error(`Failed to get response from Gemini API after ${maxRetries} attempts`);
}

export interface BatchCheckInput {
  checkId: string;
  prompt: string;
}

export interface BatchCheckResult {
  checkId: string;
  result: CheckResult;
}

/**
 * Process multiple checklist checks in a single API call with the PDF.
 * Combines up to N prompts into one structured request and parses individual results.
 */
export async function processBatchDocumentChecks(
  pdfFile: File,
  checks: BatchCheckInput[],
  model?: string,
  temperature?: number,
  abortSignal?: AbortSignal
): Promise<BatchCheckResult[]> {
  try {
    const pdfBase64 = await getPdfAsBase64(pdfFile);

    const numberedPrompts = checks.map((c, i) =>
      `=== CHECK ${i + 1}: ${c.checkId} ===\n${c.prompt}`
    ).join('\n\n');

    const batchPrompt = `You are a financial statement compliance auditor. You will perform ${checks.length} separate checks on the attached PDF document.

For EACH check below, provide your analysis in the following structured format:

--- RESULT FOR CHECK <number> ---
STATUS: COMPLIANT | NON-COMPLIANT | WARNING
EXPLANATION: <your detailed analysis>
--- END CHECK <number> ---

Important:
- Analyze each check independently based on the PDF content.
- Use exactly the delimiters shown above (--- RESULT FOR CHECK 1 ---, --- END CHECK 1 ---, etc.)
- The STATUS line must be the FIRST line after the RESULT delimiter.
- Provide thorough explanations with evidence from the document.

${numberedPrompts}`;

    let data: any;
    try {
      data = await callGeminiAPI(batchPrompt, pdfBase64, 3, normalizeModelId(model), temperature, abortSignal);
    } catch (err) {
      const message = String(err);
      if (message.includes('404')) {
        console.warn(`Model ${model} not found (404). Falling back to default model.`);
        data = await callGeminiAPI(batchPrompt, pdfBase64, 3, 'gemini-2.5-flash', temperature, abortSignal);
      } else {
        throw err;
      }
    }

    const responseText = data.candidates[0].content.parts[0].text;
    console.log(`Batch response length: ${responseText.length} chars for ${checks.length} checks`);

    const results: BatchCheckResult[] = [];

    for (let i = 0; i < checks.length; i++) {
      const checkNum = i + 1;
      const startPattern = new RegExp(`---\\s*RESULT FOR CHECK ${checkNum}\\s*---`, 'i');
      const endPattern = new RegExp(`---\\s*END CHECK ${checkNum}\\s*---`, 'i');

      const startMatch = startPattern.exec(responseText);
      let section = '';

      if (startMatch) {
        const afterStart = responseText.substring(startMatch.index + startMatch[0].length);
        const endMatch = endPattern.exec(afterStart);
        section = endMatch ? afterStart.substring(0, endMatch.index).trim() : afterStart.trim();
      }

      if (section) {
        const parsed = parseBatchSection(section, checks[i].checkId);
        results.push({ checkId: checks[i].checkId, result: parsed });
      } else {
        console.warn(`Could not parse section for check ${checkNum} (${checks[i].checkId}), attempting fallback`);
        results.push({
          checkId: checks[i].checkId,
          result: parseGeminiResponse(responseText, checks[i].checkId)
        });
      }
    }

    return results;

  } catch (error) {
    console.error(`Error processing batch checks:`, error);
    return checks.map(c => ({
      checkId: c.checkId,
      result: {
        isCompliant: false,
        explanation: `Error processing batch check: ${error instanceof Error ? error.message : String(error)}`,
        confidence: 0.5,
        extractedData: null,
        detail: ""
      }
    }));
  }
}

function parseBatchSection(section: string, checkId: string): CheckResult {
  const lines = section.split('\n').map(l => l.trim()).filter(l => l);

  let isCompliant = false;
  let isWarning = false;
  let explanation = '';

  const statusLine = lines.find(l => /^STATUS\s*:/i.test(l));
  if (statusLine) {
    const status = statusLine.replace(/^STATUS\s*:\s*/i, '').trim().toLowerCase();
    if (status.includes('warning') || status === 'check') {
      isWarning = true;
    } else if (status.includes('non-compliant') || status.includes('non compliant')) {
      isCompliant = false;
    } else if (status.includes('compliant')) {
      isCompliant = true;
    }
  }

  const explanationIdx = lines.findIndex(l => /^EXPLANATION\s*:/i.test(l));
  if (explanationIdx !== -1) {
    const firstPart = lines[explanationIdx].replace(/^EXPLANATION\s*:\s*/i, '');
    explanation = [firstPart, ...lines.slice(explanationIdx + 1)].join('\n').trim();
  } else {
    explanation = lines.filter(l => !/^STATUS\s*:/i.test(l)).join('\n').trim();
  }

  if (!explanation || explanation.length < 10) {
    explanation = 'Analysis completed based on document review.';
  }

  return {
    isCompliant,
    isWarning,
    explanation,
    detail: explanation,
    confidence: 0.8,
    extractedData: null
  };
}

/**
 * Extract all text from a PDF using a single Gemini API call.
 * This allows subsequent checks to use the text instead of re-uploading the PDF.
 */
export async function extractTextFromPDF(
  pdfFile: File,
  abortSignal?: AbortSignal
): Promise<string> {
  const pdfBase64 = await getPdfAsBase64(pdfFile);

  const prompt = `Extract ALL text content from this PDF document. 
Return the complete text exactly as it appears in the document, preserving:
- All headings, paragraphs, and sections
- All tables (format as structured text with clear column separations)
- All numerical data, financial figures, and amounts
- All footnotes, notes, and disclosures
- All page numbers and headers/footers

Do NOT summarize, interpret, or omit anything. Return the raw, complete text content.`;

  const data = await callGeminiAPI(prompt, pdfBase64, 3, 'gemini-2.5-flash', 0.1, abortSignal);
  const responseText = data.candidates[0].content.parts[0].text;
  console.log(`Extracted ${responseText.length} characters of text from PDF`);
  return responseText;
}

/**
 * Process a checklist check using pre-extracted text (no PDF upload).
 */
export async function processTextBasedCheck(
  extractedText: string,
  checkType: string,
  customPrompt: string,
  model?: string,
  temperature?: number,
  abortSignal?: AbortSignal
): Promise<CheckResult> {
  try {
    const fullPrompt = `DOCUMENT TEXT:\n\n${extractedText}\n\n---\n\nINSTRUCTIONS:\n${customPrompt}`;

    let data: any;
    try {
      data = await callGeminiAPITextOnly(fullPrompt, 3, normalizeModelId(model), temperature, abortSignal);
    } catch (err) {
      const message = String(err);
      if (message.includes('404')) {
        console.warn(`Model ${model} not found (404). Falling back to default model.`);
        data = await callGeminiAPITextOnly(fullPrompt, 3, 'gemini-2.5-flash', temperature, abortSignal);
      } else {
        throw err;
      }
    }

    const responseText = data.candidates[0].content.parts[0].text;

    if (checkType.includes('_ob') || checkType.includes('cb_py')) {
      return {
        isCompliant: true,
        explanation: responseText.trim(),
        detail: responseText.trim()
      };
    }

    return parseGeminiResponse(responseText, checkType);

  } catch (error) {
    console.error(`Error processing ${checkType} text-based check:`, error);
    return {
      isCompliant: false,
      explanation: `Error processing check: ${error instanceof Error ? error.message : String(error)}`,
      confidence: 0.5,
      extractedData: null,
      detail: ""
    };
  }
}

/**
 * Re-evaluate the compliance status of all checks by reading their analysis text.
 * Returns a map of checkId → corrected status.
 */
export async function reEvaluateComplianceStatuses(
  results: Record<string, { details?: string; compliant?: boolean; isWarning?: boolean }>,
  abortSignal?: AbortSignal
): Promise<Record<string, 'COMPLIANT' | 'NON-COMPLIANT' | 'CHECK' | 'DATA_UNAVAILABLE'>> {
  const entries = Object.entries(results).filter(([id]) => !id.startsWith('user_question_'));
  if (entries.length === 0) return {};

  const formattedChecks = entries.map(([checkId, r], i) => {
    const text = (r.details || '').substring(0, 2000);
    return `[${i + 1}] CHECK_ID: ${checkId}\nCURRENT_STATUS: ${r.isWarning ? 'CHECK' : r.compliant ? 'COMPLIANT' : 'NON-COMPLIANT'}\nANALYSIS:\n${text}`;
  }).join('\n\n---\n\n');

  const prompt = `You are a compliance status evaluator. Below are ${entries.length} checklist results from an audit analysis. Each has a CHECK_ID, a CURRENT_STATUS, and the detailed ANALYSIS text.

Your task: Read the ANALYSIS text carefully for each check and determine the CORRECT status. The analysis text is the ground truth — if the analysis concludes something is compliant but the status says NON-COMPLIANT, correct it.

Rules:
- COMPLIANT: The analysis confirms the item meets requirements / is properly disclosed / no issues found.
- NON-COMPLIANT: The analysis identifies clear deficiencies, missing disclosures, or non-compliance.
- CHECK: The analysis is inconclusive, flags something for manual review, or is a warning/observation rather than a pass/fail.
- DATA_UNAVAILABLE: Use this status ONLY when the analysis EXPLICITLY states that the check could NOT be performed AT ALL because the required document or data was entirely missing/not uploaded. The analysis must clearly say something like "document not uploaded", "document not provided", "required document not available", or "cannot perform this check without the document". Do NOT use DATA_UNAVAILABLE if the analysis contains any actual findings, calculations, comparisons, or substantive review — even if some minor detail was not found. If the check WAS performed and produced results (even partial), use COMPLIANT, NON-COMPLIANT, or CHECK instead.

SPECIAL RULE — The following checks must NEVER be assigned NON-COMPLIANT. Use only COMPLIANT or CHECK for them:
- eps_pat_ratio_comparison
- line_items_comparison
- standalone_consolidated_notes_interlink
- subsidiary_word_highlighting
- castings_check_horizontal
- basic_logic_issues
- tax_pbt_ratio_highlighting
- profit_loss_quarter_ytd_consistency
- qualification_eom_consistency
If you would assign NON-COMPLIANT to any of these, use CHECK instead.

Output format — return ONLY a JSON array, no markdown fences, no explanation:
[{"id":"<CHECK_ID>","status":"COMPLIANT|NON-COMPLIANT|CHECK|DATA_UNAVAILABLE"}]

Return one entry per check in the same order.

${formattedChecks}`;

  try {
    const data = await callGeminiAPITextOnly(prompt, 3, 'gemini-2.5-flash', 0.1, abortSignal);
    const responseText = data.candidates[0].content.parts[0].text;

    const jsonMatch = responseText.match(/\[[\s\S]*\]/);
    if (!jsonMatch) {
      console.warn('Re-evaluation: Could not parse JSON from response');
      return {};
    }

    const parsed: Array<{ id: string; status: string }> = JSON.parse(jsonMatch[0]);
    const statusMap: Record<string, 'COMPLIANT' | 'NON-COMPLIANT' | 'CHECK' | 'DATA_UNAVAILABLE'> = {};

    for (const item of parsed) {
      const normalized = item.status?.toUpperCase().trim().replace(/\s+/g, '_');
      if (normalized === 'COMPLIANT' || normalized === 'NON-COMPLIANT' || normalized === 'CHECK' || normalized === 'DATA_UNAVAILABLE') {
        statusMap[item.id] = normalized;
      }
    }

    console.log(`Re-evaluation complete: ${Object.keys(statusMap).length} statuses updated`);
    return statusMap;
  } catch (error) {
    console.error('Error during compliance re-evaluation:', error);
    return {};
  }
}

/**
 * Validate final compliance status by reviewing all analysis results
 */
export async function validateFinalComplianceStatus(
  allResults: Record<string, any>,
  abortSignal?: AbortSignal
): Promise<{ isValidatedCompliant: boolean; validationExplanation: string }> {
  try {
    console.log('🔍 Starting final compliance status validation...');

    // Format all results for AI review
    const resultsSummary: string[] = [];
    let compliantCount = 0;
    let nonCompliantCount = 0;

    Object.entries(allResults).forEach(([checkId, result]) => {
      const status = result.compliant ? 'COMPLIANT' : 'NON-COMPLIANT';
      if (result.compliant) compliantCount++;
      else nonCompliantCount++;

      resultsSummary.push(`
CHECK: ${checkId}
STATUS: ${status}
DETAILS: ${result.details || result.explanation || 'No details provided'}
${result.feedback?.reasoning ? `REASONING: ${result.feedback.reasoning}` : ''}
${result.feedback?.evidence?.length ? `EVIDENCE: ${result.feedback.evidence.join('; ')}` : ''}
${result.feedback?.recommendations?.length ? `RECOMMENDATIONS: ${result.feedback.recommendations.join('; ')}` : ''}
---`);
    });

    const prompt = `You are reviewing compliance analysis results to validate the final compliance status.

CRITICAL TASK: Review each check's analysis, reasoning, evidence, and recommendations to determine if the overall compliance status is correctly determined.

CURRENT STATUS SUMMARY:
- Total Checks: ${Object.keys(allResults).length}
- Marked as Compliant: ${compliantCount}
- Marked as Non-Compliant: ${nonCompliantCount}
- Compliance Percentage: ${Math.round((compliantCount / Object.keys(allResults).length) * 100)}%

INDIVIDUAL CHECK RESULTS:
${resultsSummary.join('\n')}

ANALYSIS INSTRUCTIONS:
1. Review each check's DETAILS, REASONING, EVIDENCE, and RECOMMENDATIONS
2. Look for inconsistencies where:
   - The analysis/reasoning says "compliant" but the status is marked "NON-COMPLIANT"
   - The analysis/reasoning says "non-compliant" or mentions issues but the status is marked "COMPLIANT"
   - Recommendations suggest fixes but status is "COMPLIANT"
   - Evidence supports compliance but status is "NON-COMPLIANT"

3. Based on the ACTUAL CONTENT of the analysis (not just the status flags), determine:
   - If the overall compliance status should be COMPLIANT or NON-COMPLIANT
   - Provide a clear explanation for your determination

OUTPUT FORMAT (STRICT):
First line: "COMPLIANT" or "NON-COMPLIANT"
Second line: "EXPLANATION: [Your detailed explanation]"

The explanation should:
- Identify any inconsistencies found between status flags and analysis content
- Explain why the overall status is correct or incorrect
- Reference specific checks that contribute to the determination
- Be clear and concise`;

    console.log('📤 Sending validation request to AI...');
    const data = await callGeminiAPITextOnly(prompt, 3, 'gemini-2.5-flash', 0.2, abortSignal);
    const responseText = data.candidates[0].content.parts[0].text;
    console.log('📥 Validation response:', responseText);

    // Parse response
    const lines = responseText.split('\n').map(l => l.trim()).filter(l => l);
    const firstLine = lines[0]?.toUpperCase().trim() || '';
    const isValidatedCompliant = firstLine === 'COMPLIANT' || firstLine.includes('COMPLIANT');
    
    // Extract explanation
    const explanationLine = lines.find(l => l.toUpperCase().startsWith('EXPLANATION:'));
    const validationExplanation = explanationLine 
      ? explanationLine.replace(/^EXPLANATION:\s*/i, '')
      : lines.slice(1).join('\n').trim() || 'Validation completed based on analysis review.';

    console.log(`✅ Validation complete: ${isValidatedCompliant ? 'COMPLIANT' : 'NON-COMPLIANT'}`);

    return {
      isValidatedCompliant,
      validationExplanation
    };

  } catch (error) {
    console.error('❌ Error validating final compliance status:', error);
    // Return the original status if validation fails
    const compliantCount = Object.values(allResults).filter(r => r.compliant).length;
    const totalCount = Object.keys(allResults).length;
    const isCompliant = compliantCount > totalCount / 2;
    
    return {
      isValidatedCompliant: isCompliant,
      validationExplanation: `Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}. Using calculated status based on check results.`
    };
  }
}